import {
  Component,
  OnInit,
  Inject,
  ViewChild,
  HostListener,
} from "@angular/core";
import { NotificationService } from "../services/notification.service";
import { UserService } from "../services/user.service";
import { NotificationModel } from "../models/notification-model.model";
import { NotificationFeedItem } from "../models/notification-feed-item.model";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { Router } from "@angular/router";
import { Constant, UserRoleEnum } from "../core/constant";
import { EventEmitterService } from "../services/event-emitter.service";
import { OAuthService } from "angular-oauth2-oidc";
import { environment } from "../../environments/environment";
declare var $: any;
import { CryptographyService } from "../services/cryptography.service";
import { AddendumService } from "../services/addendum.service";
import { ComputedNotificationSeenService } from "../services/computed-notification-seen.service";

@Component({
  selector: "app-notifications",
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"],
})
export class NotificationsComponent implements OnInit {
  showDropdown = false;
  loading: boolean = false;
  userRole: number = 2; // 1 -> Global Admin , 2 -> Market Admin
  private canReceiveMessage: boolean = false;
  public notificationHistory: NotificationFeedItem[] =
    new Array<NotificationFeedItem>();
  public notificationViewHistory: NotificationFeedItem[] =
    new Array<NotificationFeedItem>();
  public notificationUnseenHistory: NotificationFeedItem[] =
    new Array<NotificationFeedItem>();
  showNotification: boolean;

  // Same standardTypeId -> route mapping already used by the "Recent Updates" widget.
  // MarketBestPractices (2) and ABUStandard (7) were mis-mapped to the Operations
  // Standard detail page (wrong data shape). LocalStandard (3) was pointed at
  // /nrbes-view/, which needs an Nrbes row id, not this standard's own id - routed
  // to the generic standard-id-keyed detail page instead.
  standardRedirectMap: any = {
    1: "/global-recommendations-detail-view/",
    2: "/global-recommendations-detail-view/",
    3: "/view-standard-detail/",
    4: "/minimum-standard-detail-view/",
    5: "/view-equipments-standard-detail/",
    6: "/view-equipments-standard-detail/",
    7: "/view-standard-detail/",
    8: "/operations-standards-detail-view/",
  };

  get userRoleEnum() {
    return UserRoleEnum;
  }

  constructor(
    private notificationHubService: NotificationService,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    private oauthService: OAuthService,
    private router: Router,
    private cryptographyService: CryptographyService,
    private eventEmitterService: EventEmitterService,
    private addendumService: AddendumService,
    private computedNotificationSeenService: ComputedNotificationSeenService
  ) {}

  userName: string;
  userEmail: string;
  userRoleName: string;
  userMarket: string;
  showUserDropdown: boolean = false;

  ngOnInit() {
    this.userRole = this.storage.get("userRole")
      ? this.cryptographyService.decryptData(this.storage.get("userRole"))
      : this.storage.get("userRole");

    let notificationGroup = this.storage.get("notificationGroup")
      ? this.cryptographyService.decryptData(
          this.storage.get("notificationGroup")
        )
      : this.storage.get("notificationGroup");
    // Basic Users now receive New Standard Published/Standard Modified, so the
    // bell is shown to every role.
    this.showNotification = true;
    this.userName = this.storage.get("userName") || "User";

    // Email resolution
    let claims: any = null;
    try {
      claims = this.oauthService.getIdentityClaims();
    } catch(e) {}
    this.userEmail = (claims && (claims.email || claims.preferred_username || claims.upn))
      || this.storage.get("userEmail")
      || (this.userName ? this.userName.toLowerCase().replace(/\s+/g, '.') + "@us.mcd.com" : "user@us.mcd.com");

    // Role resolution
    const roleId = Number(this.userRole);
    if (roleId === 1) {
      this.userRoleName = "Global Admin";
    } else if (roleId === 2) {
      this.userRoleName = "Market Admin";
    } else if (roleId === 3) {
      this.userRoleName = "Basic User";
    } else if (roleId === 4) {
      this.userRoleName = "AOW Admin";
    } else {
      this.userRoleName = "Global Admin";
    }

    // Market resolution - "countryName" is never written anywhere in storage
    // (impersonation and normal login both only store "countryId"), so resolve
    // the name from the country list instead of reading a key that's always empty.
    this.userMarket = "United States";
    const storedCountryId = this.storage.get("countryId");
    if (storedCountryId) {
      const decryptedCountryId = this.cryptographyService.decryptData(storedCountryId);
      const countryId = Array.isArray(decryptedCountryId)
        ? decryptedCountryId[0]
        : decryptedCountryId;
      if (countryId) {
        this.addendumService.getAllCountries().subscribe((countries) => {
          const match = countries.find((c) => c.id === Number(countryId));
          if (match) {
            this.userMarket = match.name;
          }
        });
      }
    }

    // Basic Users have an empty notificationGroup (they're never assigned a
    // country/global group), but they still receive New Standard
    // Published/Modified - that category doesn't depend on notificationGroup at
    // all - so the feed still needs to load for them.
    this.getAlreadyReservedUserNotifications(notificationGroup);
    this.subscribeToEvents();
    if (this.eventEmitterService.subsVar == undefined) {
      this.eventEmitterService.subsVar =
        this.eventEmitterService.invokeGetAlreadyReservedUserNotificationsEmit.subscribe(
          () => {
            this.getAlreadyReservedUserNotifications(notificationGroup);
          }
        );
    }

    $(document).off("focusout", ".mcd-notify-icon");
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".notification-item")) {
      this.showDropdown = false;
      $(".dropdownMenu").slideUp();
    }
    if (!target.closest(".user-profile-wrapper")) {
      this.showUserDropdown = false;
    }
  }

  toggleUserDropdown(event: Event) {
    event.stopPropagation();
    this.showUserDropdown = !this.showUserDropdown;
    if (this.showUserDropdown) {
      this.showDropdown = false;
      $(".dropdownMenu").slideUp();
    }
  }

  getUserAvatarColor(name: string): string {
    if (!name) return "#8b5cf6";
    const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#64748b", "#f59e0b"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  getNotificationAvatarColor(item: NotificationFeedItem, index?: number): string {
    const colors = [
      "#10b981", // Light Green
      "#8b5cf6", // Purple
      "#f59e0b", // Amber
      "#ef4444", // Coral Red
      "#3b82f6", // Blue
      "#64748b"  // Grey
    ];
    const str = (item?.notifiedUser || '') + (item?.notificationId || '') + (item?.message || '') + (index !== undefined ? index : '');
    if (!str) return colors[(index || 0) % colors.length];

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  getSingleInitial(name: string): string {
    if (!name) return "U";
    return name.trim().charAt(0).toUpperCase();
  }

  getTimeAgo(date: Date): string {
    if (!date) return "15 mins ago";
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "15 mins ago";
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hrs ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    const d = new Date(date);
    return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
  }

  markAllAsRead() {
    if (this.notificationUnseenHistory && this.notificationUnseenHistory.length > 0) {
      this.notificationUnseenHistory.forEach((item) => {
        // Only stored exception-status notifications have a notificationId to
        // mark as seen - the two computed categories have no row to persist against.
        if (item.notificationId) {
          item.isSeen = true;
          const model = new NotificationModel();
          model.id = item.notificationId;
          model.isSeen = true;
          this.notificationHubService.UpdateNotification(model).subscribe();
        }
      });
      this.notificationUnseenHistory = [];
    }
  }

  toggleNotificationDropdown(event: Event) {
    event.stopPropagation();
    this.showUserDropdown = false; // Close user profile modal if open
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      $(".dropdownMenu").slideDown();
    } else {
      $(".dropdownMenu").slideUp();
    }
  }

  /**
   * Subscribe to receive notification.
   * @return void
   */
  private subscribeToEvents(): void {
    this.notificationHubService.connectionEstablished.subscribe(() => {
      this.canReceiveMessage = true;
      //Get NotificationGroups By Email

      if (this.userRole == this.userRoleEnum.MarketAdmin) {
        let notificationGroup = this.cryptographyService.decryptData(
          this.storage.get("notificationGroup")
        );
        this.joingToNotificationGroup(notificationGroup);
      } else if (this.userRole == this.userRoleEnum.GlobalAdmin) {
        this.joingToNotificationGroup(Constant.GlobalNotificationGroupName);
      }
    });
    this.notificationHubService.notificationReceived.subscribe(
      (notification: NotificationModel) => {
        if (notification) {
          // Live pushes are always a stored exception-status notification (the
          // only kind ever sent over SignalR) - convert to the unified feed shape.
          // Message is now e.g. "Standard Exception Approved" / "Equipment Exception
          // Rejected" - match on the status word rather than the old exact strings.
          const feedItem = new NotificationFeedItem();
          const msg = notification.message || "";
          feedItem.category = msg.includes("Approved")
            ? "ExceptionApproved"
            : msg.includes("Rejected")
              ? "ExceptionRejected"
              : msg.includes("Expired")
                ? "ExceptionExpired"
                : "ExceptionStatus";
          feedItem.message = notification.message;
          feedItem.description = notification.addendumDescription;
          feedItem.date = notification.date;
          feedItem.isSeen = notification.isSeen;
          feedItem.notificationId = notification.id;
          feedItem.addendumId = notification.addendumId;
          feedItem.notifiedUser = notification.notifiedUser;
          feedItem.notifiedUserRole = notification.notifiedUserRole;

          if (this.notificationViewHistory.length >= 5) {
            this.notificationViewHistory.splice(4, 1);
          }
          this.notificationViewHistory.unshift(feedItem);
          this.notificationHistory.unshift(feedItem);
          this.notificationUnseenHistory.unshift(feedItem);
        }
      }
    );
  }

  /**
   * Joing to notification group;
   * @param {string} groupId The id of the notification group to invoke.
   * @return void
   */
  private joingToNotificationGroup(groupId: string) {
    if (this.canReceiveMessage) {
      this.notificationHubService.joingToNotificationGroup(groupId);
    }
  }

  clearStorageAndCookiesAndInitLogout() {
    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }

    if (window.localStorage) {
      window.localStorage.clear();
    }

    document.cookie.split(";").forEach(function (cookie) {
      var eqPos = cookie.indexOf("=");
      var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
    });

    this.oauthService.logOut();
  }

  signOut() {
    this.clearStorageAndCookiesAndInitLogout();
  }

  /**
   * Get Already Reserved User Notifications;
   * @param {string} email User email.
   * @return void
   */
  public getAlreadyReservedUserNotifications(notificationGroup: string) {
    this.loading = true;
    let userId = this.cryptographyService.decryptData(
      this.storage.get("userId")
    );
    this.notificationHubService
      .getUserNotificationFeed(notificationGroup, userId, this.userRole, 0, 20)
      .subscribe((data) => {
        this.notificationHistory = [];
        this.notificationViewHistory = [];
        this.notificationUnseenHistory = [];
        data.notifications.forEach((item) => {
          const feedItem = new NotificationFeedItem(item);
          if (!feedItem.notificationId) {
            // Computed category - no stored row, so "seen" is tracked client-side.
            feedItem.isSeen = this.computedNotificationSeenService.isSeen(feedItem);
          }
          this.notificationHistory.push(feedItem);
        });
        this.notificationViewHistory = this.notificationHistory.map(
          (o) => new NotificationFeedItem(o)
        );
        this.notificationViewHistory = this.notificationViewHistory.splice(
          0,
          5
        );
        this.notificationUnseenHistory = this.notificationHistory.filter(
          (obj) => obj.isSeen == false
        );
        this.loading = false;
      });
  }

  rowSelected(item: NotificationFeedItem) {
    this.removePopOver();

    if (item.notificationId) {
      item.isSeen = true;
      const model = new NotificationModel();
      model.id = item.notificationId;
      model.isSeen = true;
      this.notificationHubService.UpdateNotification(model).subscribe(() => {
        let notificationGroup = this.cryptographyService.decryptData(
          this.storage.get("notificationGroup")
        );
        this.getAlreadyReservedUserNotifications(notificationGroup);
      });
    } else {
      // Computed category - no row to persist against, track "seen" client-side.
      item.isSeen = true;
      this.computedNotificationSeenService.markSeen(item);
    }

    if (
      item.category === "NewStandardPublished" ||
      item.category === "StandardModified"
    ) {
      const base =
        this.standardRedirectMap[item.standardTypeId] ||
        "/view-standard-detail/";
      this.router.navigate([base + item.standardId]);
    } else {
      this.router.navigate(["/addendum/" + item.addendumId]);
    }
  }

  removePopOver() {
    $(".dropdownMenu").slideUp();
  }
}
